import { useServiceOrders } from "@features/cx/consumerManagement/hooks";
import { Complaint } from "@features/cx/consumerManagement/types";
import { GenericTable } from "@shared/components/GenericTable"; // Adjust import path as needed
import { SortConfig, TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Eye } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface ServiceTabProps {
  consumerId: string;
  consumerNo: string;
  remoteUtilityId: number;
}

export const ServiceTab: React.FC<ServiceTabProps> = ({
  consumerId,
  consumerNo,
  remoteUtilityId,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useServiceOrders(consumerId, {
    page: currentPage,
    limit: PAGE_SIZE,
    query: searchTerm,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    remote_utility_id: remoteUtilityId,
  });

  const navigate = useNavigate();
  const details = data?.results;

const flattenedServiceOrders = useMemo(() => {
  if (!details) return [];

  return details.map(({ utilitySupportRequest, ...rest }) => ({
    ...rest,
    ...(utilitySupportRequest || {}), // Merge the nested object if it exists
  }));
}, [details]);

  // Define table columns
  const columns: TableColumn<Complaint>[] = [
    {
      key: "requestNo",
      header: "Service Request ID",
      sortable: true,
    },
    {
      key: "name",
      header: "Service Type",
      sortable: true,
    },
    {
      key: "supportRequestType",
      header: "Category",
      sortable: true,
    },
    {
      key: "supportRequestSubtype",
      header: "Sub Category",
      sortable: true,
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "APPROVED":
              return "default";
            case "COMPLETED":
              return "default";
            case "PENDING":
              return "secondary";
            case "CREATED":
              return "outline";
            case "CANCELLED":
              return "destructive";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "APPROVED":
              return "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200";
            case "COMPLETED":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "CREATED":
              return "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200";
            case "PENDING":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            case "CANCELLED":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "createdDate",
      header: "Created On",
      sortable: true,
    },
  ];

    const actions: TableAction<any>[] = [
      {
            label: "View",
            icon: <Eye className="h-4 w-4" />,
            onClick: (row: any) => { row.statusDisplay === 'COMPLETED' || row.statusDisplay === 'REJECTED' ? navigate(`/cx/services/history/${row.id}`) :navigate(`/cx/services/current/${row.id}`) },
          },
    ];

  // Handle sorting
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  return (
    <div className="space-y-4">
      {/* Generic Table */}
      <GenericTable
        data={flattenedServiceOrders}
        columns={columns}
        actions={actions}
        actionsType="icons"
        rowKey="id"
        pagination={{
          currentPage: data?.page || 1,
          totalPages: data?.total_pages || 1,
          totalItems: data?.count || 0,
          itemsPerPage: data?.limit || 10,
          startItem: data?.page ? (data.page - 1) * (data.limit || 10) + 1 : 0,
          endItem: data?.page
            ? Math.min(data.page * (data.limit || 10), data.count || 0)
            : 0,
        }}
        onPageChange={handlePageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchPlaceholder="Search request data..."
        searchParamName="search_data"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No service requests found"
        getRowClassName={(row, index) => {
          // Add custom row styling if needed
          return row.status === 1 ? "opacity-60" : "";
        }}
      />
    </div>
  );
};
